import { useToast } from "@uzverse/ui";
import type { EffortLevel, Locale, ModelTier } from "@uzverse/core";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EFFORTS,
  assistantTiers,
  canChooseForAssistant,
  canOverridePerMessage,
  modelLabel,
  tierModelId,
  useModelPolicy,
} from "../state/modelPolicy.js";
import type { ModelPolicyView } from "../state/modelPolicy.js";
import { useErrorText } from "./common.js";

/** "Fast: Haiku 5.5" for a tier of this company's policy. */
export function tierLabel(
  t: (key: string, vars?: Record<string, unknown>) => string,
  view: ModelPolicyView,
  tier: ModelTier,
  locale: Locale,
): string {
  const id = tierModelId(view, tier);
  const name = t(`modelPick.tier.${tier}`);
  return id ? `${name} · ${modelLabel(view, id, locale)}` : name;
}

/**
 * The user's assistant model and effort among what the company's policy allows, plus "auto"
 * (the orchestrator decides). Saved immediately as the user's `engine.assistant.*` settings.
 */
export function AssistantModelPicker({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation("web");
  const toast = useToast();
  const errorText = useErrorText();
  const { view, setAssistant } = useModelPolicy();
  const id = useId();
  const locale: Locale = i18n.language === "en" ? "en" : "tr";
  if (!view) return <span className="skeleton" />;
  const locked = !canChooseForAssistant(view);
  const tiers = assistantTiers(view);
  const known = view.assistantModel === "auto" || tiers.includes(view.assistantModel as ModelTier);
  const save = async (patch: Parameters<typeof setAssistant>[0]) => {
    try {
      await setAssistant(patch);
      toast.show(t("context.saved"));
    } catch (err) {
      toast.show(errorText(err), "error");
    }
  };
  return (
    <div className={`stack model-picker${compact ? " is-compact" : ""}`}>
      <label className="field" htmlFor={`${id}-model`}>
        <span>{t("modelPick.model")}</span>
        <select
          id={`${id}-model`}
          value={view.assistantModel}
          disabled={locked}
          onChange={(e) => void save({ model: e.target.value })}
        >
          <option value="auto">{t("modelPick.auto")}</option>
          {tiers.map((tier) => (
            <option key={tier} value={tier}>
              {tierLabel(t, view, tier, locale)}
            </option>
          ))}
          {!known && (
            <option value={view.assistantModel}>
              {modelLabel(view, view.assistantModel, locale)}
            </option>
          )}
        </select>
        <small className="muted">
          {view.assistantModel === "auto"
            ? t("modelPick.autoHint")
            : t(
                `modelPick.tierHint.${tiers.includes(view.assistantModel as ModelTier) ? view.assistantModel : "strong"}`,
              )}
        </small>
      </label>
      <label className="field" htmlFor={`${id}-effort`}>
        <span>{t("modelPick.effort")}</span>
        <select
          id={`${id}-effort`}
          value={view.assistantEffort}
          disabled={locked}
          onChange={(e) => void save({ effort: e.target.value as "auto" | EffortLevel })}
        >
          <option value="auto">{t("modelPick.auto")}</option>
          {EFFORTS.map((level) => (
            <option key={level} value={level}>
              {t(`modelPick.effortLevel.${level}.name`)}
            </option>
          ))}
        </select>
        <small className="muted" data-testid="effort-hint">
          {view.assistantEffort === "auto"
            ? t("modelPick.effortAutoHint")
            : t(`modelPick.effortLevel.${view.assistantEffort}.hint`)}
        </small>
      </label>
      {locked && <small className="muted">{t("modelPick.locked")}</small>}
      {view.policy.benchmarkHint && !compact && (
        <small className="muted model-benchmark">
          {t("modelPick.benchmark")}: {view.policy.benchmarkHint}
        </small>
      )}
    </div>
  );
}

/** A per-message choice sent with the task request. `undefined` fields mean "automatic". */
export interface MessagePreference {
  model?: ModelTier;
  effort?: EffortLevel;
}

/**
 * The small model pill next to the composer's actions: Otomatik / Hızlı / Standart / Güçlü
 * and an effort level, valid for the next message only.
 */
export function MessageModelPill({
  value,
  onChange,
}: {
  value: MessagePreference;
  onChange: (next: MessagePreference) => void;
}) {
  const { t, i18n } = useTranslation("web");
  const { view } = useModelPolicy();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const locale: Locale = i18n.language === "en" ? "en" : "tr";
  if (!view || !canOverridePerMessage(view)) return null;
  const tiers = assistantTiers(view);
  const auto = value.model === undefined && value.effort === undefined;
  const summary = auto
    ? t("modelPick.pillAuto")
    : [
        value.model ? t(`modelPick.tier.${value.model}`) : null,
        value.effort ? t(`modelPick.effortLevel.${value.effort}.name`) : null,
      ]
        .filter(Boolean)
        .join(" · ");
  return (
    <div
      className="model-pill-wrap"
      ref={wrap}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
      onBlur={(e) => {
        if (open && !wrap.current?.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        className={`btn btn-quiet btn-sm model-pill${auto ? "" : " is-on"}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t("modelPick.pillLabel")}
        title={t("modelPick.pillTitle")}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">✦</span> <span data-testid="model-pill-summary">{summary}</span>
      </button>
      {open && (
        <div className="model-menu" id={menuId} role="menu" aria-label={t("modelPick.pillLabel")}>
          <p className="eyebrow">{t("modelPick.model")}</p>
          {[undefined, ...tiers].map((tier) => (
            <button
              key={tier ?? "auto"}
              type="button"
              role="menuitemradio"
              aria-checked={value.model === tier}
              className="model-menu-item"
              onClick={() => {
                const { model: _drop, ...rest } = value;
                onChange(tier ? { ...rest, model: tier } : rest);
              }}
            >
              <span>{tier ? tierLabel(t, view, tier, locale) : t("modelPick.pillAuto")}</span>
            </button>
          ))}
          <p className="eyebrow">{t("modelPick.effort")}</p>
          {[undefined, ...EFFORTS].map((level) => (
            <button
              key={level ?? "auto"}
              type="button"
              role="menuitemradio"
              aria-checked={value.effort === level}
              className="model-menu-item"
              title={
                level ? t(`modelPick.effortLevel.${level}.hint`) : t("modelPick.effortAutoHint")
              }
              onClick={() => {
                const { effort: _drop, ...rest } = value;
                onChange(level ? { ...rest, effort: level } : rest);
              }}
            >
              <span>{level ? t(`modelPick.effortLevel.${level}.name`) : t("modelPick.auto")}</span>
            </button>
          ))}
          <small className="muted">{t("modelPick.pillNote")}</small>
        </div>
      )}
    </div>
  );
}
